import React, { useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { Image } from 'components/core/Image';
import { H3 } from 'components/core/Typography';
import { Container } from 'components/core/Page';
import { Modal, ModalToolbar, ModalBody } from 'components/core/ModalV2';
import { FacilityImageNotAvailable } from 'components/facility/ImageNotAvailable';
import { Map } from 'components/search/SearchMapV2';
import { GalleryModal } from './GalleryModal';

import './FacilityGallery.scss';

export const GalleryImage = ({
	src,
	alt,
	preload = true,
	className = 'facility-gallery__image',
	fetchPriority,
	}) => (
		<Image
			src={src}
			alt={alt}
			lazyLoad={!preload}
			className={className}
			fetchPriority={fetchPriority}
		/>
	);

GalleryImage.propTypes = {
	src: PropTypes.string.isRequired,
	alt: PropTypes.string,
	preload: PropTypes.bool,
	className: PropTypes.string,
	fetchPriority: PropTypes.oneOf(['high', 'low', 'auto']),
};

export const MemoizedGalleryImage = React.memo(GalleryImage);

export const MapTile = ({ staticMapUrl, onMapClick }) => (
	<div
		onClick={onMapClick}
		tabIndex={0}
		className="facility-gallery__image"
		onKeyPress={(e) => e.key === 'Enter' && onMapClick()}
	>
		{staticMapUrl && (
			<GalleryImage
				src={staticMapUrl}
				alt="facilityMap"
			/>
		)}
	</div>
);

MapTile.propTypes = {
	staticMapUrl: PropTypes.string,
	onMapClick: PropTypes.func.isRequired,
};

export const MemoizedMapImage = React.memo(MapTile);

export const FacilityGallery = ({
	facility,
	images,
	facilities,
	googleMapsKey,
	searchLocation,
	phone,
	preload,
	getLocalPhoneNumberByZipCode,
	onSearch,
}) => {
	const displayImages = useMemo(() => {
		if (images?.length) return images;
		return (facility?.images || [])
			.filter((img) => img?.src || img?.url)
			.map((img) => ({
				src: img.src || img.url,
				alt: img.alt || facility?.name || 'facility image',
			}));
	}, [facility, images]);

	const handleRedoSearch = useCallback((bounds) => {
		if (bounds && onSearch) {
			const { lat: cLat, lng: cLng } = bounds.getCenter().toJSON();
			const { lat: neLat, lng: neLng } = bounds.getNorthEast().toJSON();
			const { lat: swLat, lng: swLng } = bounds.getSouthWest().toJSON();

			const swValid = !isNaN(swLat) && !isNaN(swLng);
			const neValid = !isNaN(neLat) && !isNaN(neLng);
			const centerValid = !isNaN(cLat) && !isNaN(cLng);

			if (centerValid && neValid && swValid) {
				const boundary = {
					latitude: cLat,
					longitude: cLng,
					boundary: `${swLng} ${swLat} ${neLng} ${neLat}`
				};

				onSearch({ ...boundary });
			}
		}
	}, [onSearch]);

	const [state, setState] = useState({
		galleryModal: false,
		currentIndex: 0,
		mapModalOpen: false,
	});

	const handlers = useMemo(
		() => ({
			openAtIndex: (index) =>
				setState((prev) => ({
					...prev,
					currentIndex: index,
					galleryModal: true,
				})),
			closeLightbox: () =>
				setState((prev) => ({
					...prev,
					galleryModal: false,
				})),
			showPrev: () =>
				setState((prev) => ({
					...prev,
					currentIndex:
						(prev.currentIndex - 1 + displayImages.length) %
						displayImages.length,
				})),
			showNext: () =>
				setState((prev) => ({
					...prev,
					currentIndex:
						(prev.currentIndex + 1) % displayImages.length,
				})),
			toggleMapModal: () =>
				setState((prev) => ({
					...prev,
					mapModalOpen: !prev.mapModalOpen,
				})),
		}),
		[displayImages.length]
	);

	const handleImageClick = useCallback(
		(index) => () => {
			handlers.openAtIndex(index);
		},
		[handlers]
	);

	const imageCount = displayImages.length;
	const hasImages = imageCount > 0;
	const staticMapUrl = facility?.staticMapUrl || '';

	const renderMobileBottomRow = useCallback(() => {
		const count = Math.min(imageCount, 5);

		return (
			<div
				className={classNames(
					'facility-gallery-mobile__mobileRow-bottom',
					`facility-gallery-mobile__mobileRow-bottom--count${count}`
				)}
			>
				<MapTile
					staticMapUrl={staticMapUrl}
					onMapClick={handlers.toggleMapModal}
				/>

				{imageCount >= 2 &&
					displayImages
						.slice(1, imageCount >= 5 ? 3 : imageCount)
						.map((img, idx) => (
							<div
								key={`mobile-${idx + 1}`}
								className="facility-gallery__thumb"
								onClick={handleImageClick(idx + 1)}
								aria-label="Open gallery"
							>
								<GalleryImage {...img} />
							</div>
						))}

				{imageCount >= 5 && (
					<div
						className={classNames(
							'facility-gallery__thumb',
							'facility-gallery__more'
						)}
						onClick={handleImageClick(3)}
						aria-label="Open full gallery"
						style={{
							backgroundImage: `url(${displayImages[3].src})`,
						}}
					>
						<span className="facility-gallery__moreOverlay" />
						<span className="facility-gallery__moreText">
							+{' '}{imageCount - 3} Photos
						</span>
					</div>
				)}
			</div>
		);
	}, [imageCount, displayImages, staticMapUrl, handlers]);

	return (
		<Container className="facility-gallery">
			{/* Desktop Grid */}
			<div className="facility-gallery__grid">
				{!hasImages && (
					<div className="facility-gallery__featured">
						<FacilityImageNotAvailable size="large" />
					</div>
				)}

				{displayImages[0] && (
					<div
						className="facility-gallery__featured"
						onClick={handleImageClick(0)}
						aria-label="Open gallery"
					>
						<GalleryImage
							src={displayImages[0].src}
							alt={displayImages[0].alt}
							fetchPriority="high"
						/>
					</div>
				)}

				<div className="facility-gallery__rightCol">
					{!hasImages && (
						<div
							className="facility-gallery__thumb facility-gallery__mapTile"
							onClick={handlers.toggleMapModal}
						>
							{staticMapUrl && (
								<MapTile
									staticMapUrl={staticMapUrl}
									onMapClick={handlers.toggleMapModal}
								/>
							)}
						</div>
					)}
					{imageCount === 1 && (
						<div className="facility-gallery__thumb facility-gallery__mapTile_image">
							{staticMapUrl && (
								<MapTile
									staticMapUrl={staticMapUrl}
									onMapClick={handlers.toggleMapModal}
								/>
							)}
						</div>
					)}

					{imageCount === 2 && (
						<div className="facility-gallery__col2Rows">
							<div className="facility-gallery__thumb">
								{staticMapUrl && (
									<MapTile
										staticMapUrl={staticMapUrl}
										onMapClick={handlers.toggleMapModal}
									/>
								)}
							</div>
							<div
								className="facility-gallery__thumb"
								onClick={handleImageClick(1)}
								aria-label="Open gallery"
							>
								<GalleryImage
									src={displayImages[1].src}
									alt={displayImages[1].alt}
								/>
							</div>
						</div>
					)}

					{imageCount === 3 && (
						<div className="facility-gallery__mapTop_rowBottom">
							<div className="facility-gallery__thumb">
								{staticMapUrl && (
									<MapTile
										staticMapUrl={staticMapUrl}
										onMapClick={handlers.toggleMapModal}
									/>
								)}
							</div>
							<div className="facility-gallery__row2Cols">
								{displayImages.slice(1, 3).map((img, idx) => (
									<div
										key={`c3-${idx}`}
										className="facility-gallery__thumb"
										onClick={handleImageClick(idx + 1)}
										aria-label="Open gallery"
									>
										<GalleryImage
											src={img.src}
											alt={img.alt}
										/>
									</div>
								))}
							</div>
						</div>
					)}

					{imageCount === 4 && (
						<div className="facility-gallery__grid2x2">
							<div
								className="facility-gallery__thumb"
								onClick={handleImageClick(1)}
							>
								<GalleryImage
									src={displayImages[1].src}
									alt={displayImages[1].alt}
								/>
							</div>
							<div className="facility-gallery__thumb">
								{staticMapUrl && (
									<MapTile
										staticMapUrl={staticMapUrl}
										onMapClick={handlers.toggleMapModal}
									/>
								)}
							</div>
							<div
								className="facility-gallery__thumb"
								onClick={handleImageClick(2)}
							>
								<GalleryImage
									src={displayImages[2].src}
									alt={displayImages[2].alt}
								/>
							</div>
							<div
								className="facility-gallery__thumb"
								onClick={handleImageClick(3)}
								aria-label="Open gallery"
							>
								<GalleryImage
									src={displayImages[3].src}
									alt={displayImages[3].alt}
								/>
							</div>
						</div>
					)}

					{imageCount >= 5 && (
						<div className="facility-gallery__grid2x2">
							<div
								className="facility-gallery__thumb"
								onClick={handleImageClick(1)}
							>
								<GalleryImage
									src={displayImages[1].src}
									alt={displayImages[1].alt}
								/>
							</div>
							<div className="facility-gallery__thumb">
								{staticMapUrl && (
									<MapTile
										staticMapUrl={staticMapUrl}
										onMapClick={handlers.toggleMapModal}
									/>
								)}
							</div>
							<div
								className="facility-gallery__thumb"
								onClick={handleImageClick(2)}
							>
								<GalleryImage
									src={displayImages[2].src}
									alt={displayImages[2].alt}
								/>
							</div>
							<div
								className={classNames(
									'facility-gallery__thumb',
									'facility-gallery__more'
								)}
								onClick={handleImageClick(3)}
								style={{
									backgroundImage: `url(${displayImages[3].src})`,
								}}
							>
								<span className="facility-gallery__moreOverlay" />
								<span className="facility-gallery__moreText">
									+{' '}{imageCount - 3} Photos
								</span>
							</div>
						</div>
					)}
				</div>
			</div>
			{/* Mobile Layout */}
			<div className="facility-gallery-mobile__mobileRow">
				<div className="facility-gallery-mobile__mobileRow-top">
					<div
						className="facility-gallery-mobile__featured"
						onClick={handleImageClick(0)}
						aria-label="Open gallery"
					>
						<GalleryImage
							{...displayImages[0]}
							// preload={preload}
							fetchPriority="high"
						/>
					</div>
				</div>
				{renderMobileBottomRow()}
			</div>
			<Modal
				showing={state.galleryModal}
				onClose={handlers.closeLightbox}
				size="fullscreen"
				closeable={true}
				closeButtonText="Close"
				toolbar={
					<ModalToolbar>
						<H3 className="facility-gallery-modal__toolbar-title">
							{facility?.name}
						</H3>
					</ModalToolbar>
				}
			>
				<ModalBody>
					<GalleryModal
						images={displayImages}
						currentIndex={state.currentIndex}
						onPrevious={handlers.showPrev}
						onNext={handlers.showNext}
						name={facility?.name}
						onThumbnailClick={(index) =>
							setState((prev) => ({
								...prev,
								currentIndex: index,
							}))
						}
					/>
				</ModalBody>
			</Modal>
			<Modal
				showing={state.mapModalOpen}
				onClose={handlers.toggleMapModal}
				size="fullscreen"
				closeButtonText="Close"
				closeable
				toolbar={
					<ModalToolbar>
						<H3>Map View</H3>
					</ModalToolbar>
				}
			>
				<ModalBody>
					<Map
						googleMapsKey={googleMapsKey}
						facilities={facilities}
						highlightedFacility={facility.id}
						searchLocation={searchLocation}
						initialZoom={11}
						phone={phone}
						preload={true}
						showControls={true}
						getLocalPhoneNumberByZipCode={
							getLocalPhoneNumberByZipCode
						}
						onRedoSearch={handleRedoSearch}
						searchOnMove={true}
						preselectedFacilityId={facility.id}
						onReady={() => {}}
					/>
				</ModalBody>
			</Modal>
		</Container>
	);
};

FacilityGallery.propTypes = {
	facility: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		name: PropTypes.string,
		staticMapUrl: PropTypes.string,
		location: PropTypes.object,
		images: PropTypes.arrayOf(
			PropTypes.oneOfType([
				PropTypes.shape({
					src: PropTypes.string,
					url: PropTypes.string,
					alt: PropTypes.string,
				}),
				PropTypes.string,
			])
		),
	}),
	images: PropTypes.arrayOf(
		PropTypes.shape({
			src: PropTypes.string.isRequired,
			alt: PropTypes.string,
		})
	),
	className: PropTypes.string,
	preload: PropTypes.bool,
	openInteractiveMap: PropTypes.func,
	onSearch: PropTypes.func,
};

FacilityGallery.defaultProps = {
	facility: null,
	images: [],
	className: '',
	preload: true,
	openInteractiveMap: () => {},
	onSearch: () => {},
};

export default FacilityGallery;
